import { DOCUMENT, Location } from '@angular/common';
import { Injectable, inject } from '@angular/core';

import { Observable, distinctUntilChanged, map, mergeMap, tap } from 'rxjs';

import {
  Banner,
  GlobalProductsRoutes,
  MiniTeaserBanner,
  PromotionDataKeys,
  RouteData,
  TrackingPromotionData
} from '@hdms/shared/common-types';
import {
  AnalyticsService,
  EnvironmentService,
  PlatformService,
  RouterHelperService,
  getSafeURL,
  sanitizeUrl
} from '@hdms/shared/util-core';

import { CartService } from '../cart';
import { Localization, getLocalization } from '../helper';
import { UserContextService } from '../user';

const CATEGORY_ROUTE_REGEX = new RegExp(`/${GlobalProductsRoutes.BASE}/([A-Za-z_0-9]+)/*([A-Za-z_0-9]*)`);

const HILTI_DOMAIN = 'hilti.com';

@Injectable({
  providedIn: 'root'
})
export class BannerHelperService {
  private readonly routerHelper = inject(RouterHelperService);
  private readonly env = inject(EnvironmentService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly location = inject(Location);
  private readonly document = inject(DOCUMENT);
  private readonly isBrowser = inject(PlatformService).isBrowser;
  private readonly cartService = inject(CartService);
  private readonly platform = inject(PlatformService);
  private readonly isMobileApp = inject(EnvironmentService).mobileApp;
  private readonly userContext = inject(UserContextService);

  public readonly routeChange$ = this.getRouteChange$();

  public readonly canRequestBanners = this.getCanRequestBanners();

  public hasHiltiReferrer = this.getHasHiltiReferrer();

  private routeData: RouteData = {};

  get finalUrl(): string {
    return sanitizeUrl(this.routerHelper.finalUrl);
  }

  /**
   * Return RouteData.templateType or fallback to legacy tracking data provided by in-page env data.
   */
  get pageType(): string {
    return this.routeData.tracking?.templateType || this.env.trackingData.templateType;
  }

  /**
   * Return RouteData.productItemId / productRangeId or fallback to legacy tracking data provided by in-page env data.
   */
  get productId(): string {
    return (
      this.routeData.tracking?.productItemId ||
      this.env.trackingData.productItemId ||
      this.routeData.tracking?.productRangeId ||
      this.env.trackingData.productRangeId
    );
  }

  get categoryId(): string {
    const pathMatch = this.location.path().match(CATEGORY_ROUTE_REGEX);
    return pathMatch ? pathMatch[2] || pathMatch[1] : null;
  }

  get cartId(): string {
    return this.cartService.cartId;
  }

  get localization(): Localization {
    return getLocalization(this.env);
  }

  trackBannerImpression(banner: Banner, additionalProperties?: Record<string, unknown>): void {
    banner.webAnalyticsData &&
      this.analyticsService.trackPromotionEvent(PromotionDataKeys.EEC_PROMOTION_IMPRESSION, [
        this.getBannerTrackingProperties(banner, additionalProperties)
      ]);
  }

  trackBannerClick(banner: Banner, additionalProperties?: Record<string, unknown>): void {
    banner.webAnalyticsData &&
      this.analyticsService.trackPromotionEvent(PromotionDataKeys.EEC_PROMOTION_CLICK, [
        this.getBannerTrackingProperties(banner, additionalProperties)
      ]);
  }

  trackBannerClose(banner: Banner, reason: 'OptOut' | 'Close'): void {
    if (banner.webAnalyticsData) {
      const promotionTrackingProperties = this.getBannerTrackingProperties(banner);
      this.analyticsService.sendEvent({
        ...promotionTrackingProperties,
        category: 'Banner',
        action: reason,
        label: promotionTrackingProperties.id
      });
    }
  }

  trackMiniTeaserBannersImpression(miniTeaserBanners: MiniTeaserBanner[]): void {
    const promotions = miniTeaserBanners
      .filter(banner => banner.webAnalyticsData)
      .map(banner => this.getBannerTrackingProperties(banner));

    if (promotions.length) {
      this.analyticsService.trackPromotionEvent(PromotionDataKeys.EEC_PROMOTION_IMPRESSION, promotions);
    }
  }

  trackMiniTeaserBannerClick(miniTeaserBanner: MiniTeaserBanner): void {
    miniTeaserBanner.webAnalyticsData &&
      this.analyticsService.trackPromotionEvent(PromotionDataKeys.EEC_PROMOTION_CLICK, [
        this.getBannerTrackingProperties(miniTeaserBanner)
      ]);
  }

  private getBannerTrackingProperties(
    { webAnalyticsData: { trackId, trackCreative, trackName } = {} }: Banner | MiniTeaserBanner,
    additionalProperties: Record<string, unknown> = {}
  ): TrackingPromotionData {
    return {
      creative: trackCreative,
      name: trackName,
      id: trackId,
      ...additionalProperties
    };
  }

  private getRouteChange$(): Observable<string> {
    let changeCount = 0;
    return this.routerHelper.activatedRoute$.pipe(
      mergeMap(({ data }) => data),
      tap(data => {
        this.routeData = data;
        if (changeCount) {
          this.hasHiltiReferrer = true;
        }
        changeCount++;
      }),
      map(() => this.finalUrl),
      distinctUntilChanged()
    );
  }

  /**
   * Return if current page referrer origin is either current origin or ends with hilti.com
   * @private
   */
  private getHasHiltiReferrer(): boolean {
    if (this.isBrowser) {
      const { referrer, location } = this.document;
      return referrer.startsWith(location.origin) || getSafeURL(referrer).origin?.endsWith(HILTI_DOMAIN);
    }
    return false;
  }

  /**
   * Return if requesting banner data is enabled
   * @private
   */
  private getCanRequestBanners(): boolean {
    return this.platform.isBrowser && !this.isMobileApp && !this.userContext.isAccountManager;
  }
}
